import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Trash2, Loader2, X, Eye, EyeOff } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useTheme } from '../../context/ThemeContext';

/**
 * RGPD-2.3 — Modal de confirmation suppression compte.
 *
 * Confirmation forte (D8) : email exact + password.
 * Sur succès : appelle `onDeleted(summary)` (le parent gère logout + redirect).
 *
 * Props:
 *   show: bool — affichage modal
 *   onClose: () => void — fermeture sans suppression
 *   onDeleted: (summary) => void — callback post-suppression réussie
 *   userEmail: string — email actuel du user (pour comparaison UX)
 *   onSubmit: async (email, password) => summary — appel backend
 *     (provided by parent via AuthContext.deleteAccount)
 */
const DeleteAccountModal = ({ show, onClose, onDeleted, userEmail, onSubmit }) => {
  const { t } = useLanguage();
  const { isDark } = useTheme();
  const [confirmEmail, setConfirmEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const reset = () => {
    setConfirmEmail('');
    setPassword('');
    setShowPassword(false);
    setError(null);
    setSubmitting(false);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  // RGPD-2.3a-fix #2 : Escape ferme la modal (accessibilité clavier).
  // Click-outside backdrop est désactivé (cf <motion.div> backdrop).
  useEffect(() => {
    if (!show) return;
    const onKey = (e) => {
      if (e.key === 'Escape' && !submitting) {
        handleClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [show, submitting]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const summary = await onSubmit(confirmEmail, password);
      onDeleted(summary);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      // RGPD-2.3a-fix #1 : map backend codes discriminants → clés i18n.
      // Plus jamais d'affichage du detail backend brut pour 422/429 (qui
      // étaient FR-only avant fix). Les 400 (guards multi-tenants) gardent
      // le detail backend FR explicite (cas rares, V1).
      let message;
      if (status === 422 && detail === 'email_mismatch') {
        message = t('account.deleteModal.errorEmailMismatch');
      } else if (status === 422 && detail === 'wrong_password') {
        message = t('account.deleteModal.errorWrongPassword');
      } else if (status === 429) {
        message = t('account.deleteModal.errorRateLimit');
      } else if (status === 400 && typeof detail === 'string') {
        // Multi-tenants guard (D6) — message FR backend explicite (V1)
        message = detail;
      } else {
        message = t('account.deleteModal.errorGeneric');
      }
      setError(message);
      setSubmitting(false);
    }
  };

  const canSubmit = confirmEmail.trim() && password && !submitting;

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          /* RGPD-2.3a-fix #2 : click-outside désactivé (action destructive).
             Fermeture via X / Annuler / Escape uniquement. */
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={e => e.stopPropagation()}
            className={`w-full max-w-md rounded-2xl shadow-2xl ${
              isDark ? 'bg-slate-800' : 'bg-white'
            }`}
            data-testid="delete-account-modal"
          >
            {/* Header */}
            <div className={`flex items-start justify-between p-6 border-b ${
              isDark ? 'border-slate-700' : 'border-gray-100'
            }`}>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-red-500/20">
                  <AlertTriangle className="w-6 h-6 text-red-500" />
                </div>
                <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {t('account.deleteModal.title')}
                </h2>
              </div>
              <button
                type="button"
                onClick={handleClose}
                disabled={submitting}
                className={`p-2 rounded-lg transition-colors ${
                  isDark ? 'hover:bg-slate-700' : 'hover:bg-gray-100'
                } disabled:opacity-50`}
              >
                <X className={`w-5 h-5 ${isDark ? 'text-slate-400' : 'text-gray-500'}`} />
              </button>
            </div>

            {/* Body */}
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className={`p-4 rounded-xl border-2 border-red-500/30 ${
                isDark ? 'bg-red-500/10' : 'bg-red-50'
              }`}>
                <p className={`font-semibold mb-1 ${isDark ? 'text-red-400' : 'text-red-700'}`}>
                  {t('account.deleteModal.warning')}
                </p>
                <p className={`text-sm ${isDark ? 'text-red-300' : 'text-red-600'}`}>
                  {t('account.deleteModal.consequences')}
                </p>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1.5 ${
                  isDark ? 'text-slate-300' : 'text-gray-700'
                }`}>
                  {t('account.deleteModal.confirmEmailLabel')}
                </label>
                <input
                  type="text"
                  value={confirmEmail}
                  onChange={e => setConfirmEmail(e.target.value)}
                  placeholder={userEmail}
                  data-testid="delete-confirm-email"
                  autoComplete="off"
                  disabled={submitting}
                  className={`w-full px-4 py-2.5 rounded-xl border transition-all focus:ring-2 focus:ring-red-500 focus:outline-none ${
                    isDark
                      ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-500'
                      : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                  }`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1.5 ${
                  isDark ? 'text-slate-300' : 'text-gray-700'
                }`}>
                  {t('account.deleteModal.confirmPasswordLabel')}
                </label>
                {/* RGPD-2.3a-fix #4 : toggle show/hide password (pattern
                    inline cohérent AuthPage.js / ResetPassword.js / Account.js) */}
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    data-testid="delete-confirm-password"
                    autoComplete="current-password"
                    disabled={submitting}
                    className={`w-full px-4 py-2.5 pr-10 rounded-xl border transition-all focus:ring-2 focus:ring-red-500 focus:outline-none ${
                      isDark
                        ? 'bg-slate-700 border-slate-600 text-white'
                        : 'bg-white border-gray-200 text-gray-900'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    disabled={submitting}
                    data-testid="delete-toggle-password-visibility"
                    className={`absolute right-3 top-1/2 -translate-y-1/2 ${
                      isDark ? 'text-slate-400 hover:text-slate-200' : 'text-gray-400 hover:text-gray-600'
                    } disabled:opacity-50`}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {error && (
                <div
                  className={`flex items-start gap-2 p-3 rounded-xl text-sm ${
                    isDark ? 'bg-red-500/10 text-red-400' : 'bg-red-50 text-red-700'
                  }`}
                  data-testid="delete-error"
                >
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}
            </form>

            {/* Footer */}
            <div className={`flex justify-end gap-3 p-6 border-t ${
              isDark ? 'border-slate-700' : 'border-gray-100'
            }`}>
              <button
                type="button"
                onClick={handleClose}
                disabled={submitting}
                className={`px-4 py-2.5 rounded-xl font-medium transition-colors ${
                  isDark ? 'text-slate-300 hover:bg-slate-700' : 'text-gray-700 hover:bg-gray-100'
                } disabled:opacity-50`}
              >
                {t('account.deleteModal.cancel')}
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit}
                data-testid="delete-confirm-btn"
                className="flex items-center gap-2 px-5 py-2.5 bg-red-500 hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-medium transition-colors"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('account.deleteModal.deleting')}
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    {t('account.deleteModal.confirm')}
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DeleteAccountModal;
